import { approvedChangeOrderMoney } from '@/lib/data/change-orders';
import { totalsByProject, revisedBudgetCents, type ChangeOrderTotals } from '@/lib/change-order-budget';
import { listProjects, createProject } from '@/lib/data/projects';
import { esc, fmtMoney, fmtDate, setPageTitle } from '@/lib/dom';
import { resolveMediaSrc } from '@/lib/storage';
import { getShell } from '@/lib/shell-handle';
import { getSession } from '@/lib/auth';
import type { ProjectRow } from '@/types/database';

function firstNameFromEmail(email: string | null | undefined): string {
  if (!email) return 'there';
  const local = email.split('@')[0] ?? '';
  const first = local.split(/[._-]/)[0] ?? '';
  if (!first) return 'there';
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

export async function renderProjectsList(root: HTMLElement): Promise<void> {
  setPageTitle('Projects');
  getShell()?.setContext({ kind: 'global', active: 'projects' });
  const session = await getSession();
  const greeting = firstNameFromEmail(session?.user.email);
  root.innerHTML = `
    <section class="page projects-page">
      <div class="page-greeting">
        <div class="eyebrow">Welcome back</div>
        <h1>${esc(greeting)}</h1>
        <p class="muted">Pick up where you left off.</p>
      </div>

      <div class="section-bar">
        <div class="section-bar-left"><h3>Your projects</h3></div>
        <button class="btn-ghost" id="new-project-btn">+ New Project</button>
      </div>
      <div class="loading" id="projects-loading">Loading…</div>
      <div class="grid projects-grid" id="projects-grid" hidden></div>
      <div class="empty" id="projects-empty" hidden>
        <p>No projects yet.</p>
        <button class="btn-primary" id="empty-new-btn">Create your first project</button>
      </div>
    </section>
    <div class="modal" id="new-project-modal" hidden>
      <form class="modal-card" id="new-project-form">
        <h2>New project</h2>
        <label class="field"><span>Name</span><input name="name" required /></label>
        <label class="field"><span>Client name</span><input name="client_name" /></label>
        <label class="field"><span>Address</span><input name="address" /></label>
        <label class="field"><span>Description</span><textarea name="description" rows="3"></textarea></label>
        <div class="modal-actions">
          <button type="button" class="btn-link" data-close>Cancel</button>
          <button type="submit" class="btn-primary">Create</button>
        </div>
        <div class="auth-error" id="new-project-error" hidden></div>
      </form>
    </div>
  `;

  const grid = root.querySelector<HTMLElement>('#projects-grid')!;
  const loading = root.querySelector<HTMLElement>('#projects-loading')!;
  const empty = root.querySelector<HTMLElement>('#projects-empty')!;
  const modal = root.querySelector<HTMLElement>('#new-project-modal')!;

  const openModal = (): void => { modal.hidden = false; };
  const closeModal = (): void => { modal.hidden = true; };

  root.querySelector('#new-project-btn')!.addEventListener('click', openModal);
  root.querySelector('#empty-new-btn')!.addEventListener('click', openModal);
  modal.querySelector('[data-close]')!.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

  const form = root.querySelector<HTMLFormElement>('#new-project-form')!;
  const errBox = root.querySelector<HTMLDivElement>('#new-project-error')!;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errBox.hidden = true;
    const fd = new FormData(form);
    try {
      const created = await createProject({
        name: String(fd.get('name')),
        client_name: (fd.get('client_name') as string) || null,
        address: (fd.get('address') as string) || null,
        description: (fd.get('description') as string) || null,
      });
      closeModal();
      form.reset();
      location.hash = `#/projects/${created.id}`;
    } catch (err) {
      errBox.hidden = false;
      errBox.textContent = err instanceof Error ? err.message : 'Failed to create project';
    }
  });

  try {
    const [projects, approved] = await Promise.all([
      listProjects(),
      // Revised budgets (0038). A failure shows the original budgets rather
      // than no list at all.
      approvedChangeOrderMoney().catch(() => []),
    ]);
    const totals = totalsByProject(approved);
    loading.hidden = true;
    if (projects.length === 0) {
      empty.hidden = false;
    } else {
      grid.hidden = false;
      grid.innerHTML = projects.map((p) => renderCard(p, totals.get(p.id))).join('');
      grid.querySelectorAll<HTMLImageElement>('img[data-media-src]').forEach((img) => {
        const src = img.getAttribute('data-media-src')!;
        void resolveMediaSrc(src).then((url) => { if (url) img.src = url; });
      });
    }
  } catch (err) {
    loading.innerHTML = `<div class="auth-error">${esc(err instanceof Error ? err.message : 'Failed to load')}</div>`;
  }
}

function renderCard(p: ProjectRow, changes?: ChangeOrderTotals): string {
  const revised = changes ? revisedBudgetCents(p.budget_cents, changes) : p.budget_cents;
  const count = changes?.approvedCount ?? 0;
  return `
    <a class="project-card" href="#/projects/${esc(p.id)}">
      ${p.hero_image_url ? `<div class="project-card-thumb"><img data-media-src="${esc(p.hero_image_url)}" alt="" /></div>` : ''}
      <div class="project-card-body">
        <div class="project-card-eyebrow">${esc(p.client_name ?? 'No client')}</div>
        <h3>${esc(p.name)}</h3>
        ${p.address ? `<div class="project-card-addr">${esc(p.address)}</div>` : ''}
        ${p.description ? `<p class="project-card-desc">${esc(p.description)}</p>` : ''}
        <div class="project-card-meta">
          <span>${fmtDate(p.start_date)} → ${fmtDate(p.target_completion_date)}</span>
          <span>${fmtMoney(revised)}${count > 0 ? ` <span class="muted">incl. ${count} change${count === 1 ? '' : 's'}</span>` : ''}</span>
        </div>
      </div>
    </a>
  `;
}
